/* eslint-disable no-nested-ternary */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Loading from '../../components/Loading';
import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageHeaderActions,
   PageSubtitle,
   PageTitle,
} from '../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../styles/layout/Section';
import { FormGrid } from '../../styles/layout/FormGrid';
import { Field } from '../../styles/forms/Field';
import { ActionBar, ActionBarRight } from '../../styles/layout/ActionBar';
import { TableWrapper } from '../../styles/table/TableWrapper';
import { Table } from '../../styles/table/Table';
import { EmptyState } from '../../styles/feedback/EmptyState';
import PaginationBar from '../../components/PaginationBar/PaginationBar';
import SectionMeta, { SectionHeaderRow } from '../../components/SectionMeta';
import {
   CardGrid,
   MiniCard,
   MiniCardTitle,
   MiniCardValue,
} from '../../styles/layout/CardGrid';

// ─────────────────────────────────────────────────────────────────────────────
// Estilos locais
// ─────────────────────────────────────────────────────────────────────────────

const ServiceOrderStatusPill = styled.span`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   padding: 4px 10px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 700;
   white-space: nowrap;
   border: 1px solid ${({ $statusColor }) => $statusColor?.border ?? 'rgba(0,0,0,0.15)'};
   background: ${({ $statusColor }) => $statusColor?.bg ?? 'rgba(0,0,0,0.06)'};
   color: ${({ $statusColor }) => $statusColor?.text ?? '#444'};
`;

const SlaAlertBadge = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 4px;
   padding: 3px 8px;
   border-radius: 999px;
   font-size: 10px;
   font-weight: 700;
   background: rgba(183, 28, 28, 0.1);
   color: #b71c1c;
   border: 1px solid rgba(183, 28, 28, 0.25);
`;

const FilterTabBar = styled.div`
   display: flex;
   gap: 8px;
   flex-wrap: wrap;
   margin-bottom: 4px;
`;

const FilterTab = styled.button`
   padding: 5px 14px;
   border-radius: 999px;
   font-size: 12px;
   font-weight: ${({ $active }) => ($active ? '700' : '500')};
   border: 1px solid
      ${({ $active, theme }) =>
         $active
            ? (theme?.colors?.appBackground ?? '#1a237e')
            : (theme?.colors?.border ?? 'rgba(0,0,0,0.15)')};
   background: ${({ $active, theme }) =>
      $active ? (theme?.colors?.appBackground ?? '#1a237e') : 'transparent'};
   color: ${({ $active, theme }) =>
      $active ? '#fff' : (theme?.colors?.textMuted ?? '#555')};
   cursor: pointer;
   transition: all 0.15s;

   &:hover {
      border-color: ${({ theme }) => theme?.colors?.appBackground ?? '#1a237e'};
      color: ${({ $active, theme }) =>
         $active ? '#fff' : (theme?.colors?.appBackground ?? '#1a237e')};
   }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

const SERVICE_ORDER_STATUS_LABELS = {
   AGUARDANDO_RECEBIMENTO: 'Aguard. recebimento',
   EM_TRIAGEM:             'Em triagem',
   AGUARDANDO_PECAS:       'Aguard. peças',
   EM_REPARO:              'Em reparo',
   AGUARDANDO_APROVACAO:   'Aguard. aprovação',
   REPARO_CONCLUIDO:       'Reparo concluído',
   PRONTO_PARA_ENTREGA:    'Pronto p/ entrega',
   ENTREGUE:               'Entregue',
   DEVOLVIDO_SEM_REPARO:   'Devolvido s/ reparo',
};

const SERVICE_ORDER_STATUS_COLORS = {
   AGUARDANDO_RECEBIMENTO: { bg: 'rgba(0,0,0,0.06)',      border: 'rgba(0,0,0,0.15)',      text: '#444' },
   EM_TRIAGEM:             { bg: 'rgba(83,74,183,0.10)',  border: 'rgba(83,74,183,0.30)',  text: '#3c3489' },
   AGUARDANDO_PECAS:       { bg: 'rgba(186,117,23,0.12)', border: 'rgba(186,117,23,0.30)', text: '#854f0b' },
   EM_REPARO:              { bg: 'rgba(24,95,165,0.10)',  border: 'rgba(24,95,165,0.30)',  text: '#0c447c' },
   AGUARDANDO_APROVACAO:   { bg: 'rgba(216,90,48,0.10)',  border: 'rgba(216,90,48,0.30)',  text: '#712b13' },
   REPARO_CONCLUIDO:       { bg: 'rgba(15,110,86,0.10)',  border: 'rgba(15,110,86,0.30)',  text: '#085041' },
   PRONTO_PARA_ENTREGA:    { bg: 'rgba(59,109,17,0.10)',  border: 'rgba(59,109,17,0.30)',  text: '#27500a' },
   ENTREGUE:               { bg: 'rgba(15,110,86,0.08)',  border: 'rgba(15,110,86,0.20)',  text: '#085041' },
   DEVOLVIDO_SEM_REPARO:   { bg: 'rgba(0,0,0,0.06)',      border: 'rgba(0,0,0,0.15)',      text: '#5f5e5a' },
};

const STATUS_FILTER_OPTIONS = [
   { value: '',                      label: 'Todos' },
   { value: 'AGUARDANDO_RECEBIMENTO',label: 'Aguard. recebimento' },
   { value: 'EM_TRIAGEM',            label: 'Em triagem' },
   { value: 'AGUARDANDO_PECAS',      label: 'Aguard. peças' },
   { value: 'EM_REPARO',             label: 'Em reparo' },
   { value: 'AGUARDANDO_APROVACAO',  label: 'Aguard. aprovação' },
   { value: 'PRONTO_PARA_ENTREGA',   label: 'Pronto p/ entrega' },
   { value: 'REPARO_CONCLUIDO',      label: 'Concluídas' },
   { value: 'ENTREGUE',              label: 'Entregues' },
];

const ITEMS_PER_PAGE = 15;

// ─────────────────────────────────────────────────────────────────────────────
// Dados mock — serão substituídos por chamadas à API
// ─────────────────────────────────────────────────────────────────────────────

const MOCK_SERVICE_ORDERS = [
   {
      id: 47,
      os_number: 'OS-2026-0047',
      service_order_status: 'EM_REPARO',
      equipment_description: 'Purificador Soft Plus',
      pcr_serial_number: 'SP-000123',
      requester_name: 'João Ferreira',
      technician_name: 'Lucas Teixeira',
      sla_due_date: '2026-03-28',
      sla_is_paused: false,
      is_sla_overdue: false,
      opened_at: '2026-03-20',
      updated_at: '2026-03-26T09:14:00',
      tmr_days: 5,
   },
   {
      id: 46,
      os_number: 'OS-2026-0046',
      service_order_status: 'PRONTO_PARA_ENTREGA',
      equipment_description: 'Purificador Master Frio',
      pcr_serial_number: null,
      requester_name: 'Maria Oliveira',
      technician_name: 'Ana Rodrigues',
      sla_due_date: '2026-03-25',
      sla_is_paused: false,
      is_sla_overdue: false,
      opened_at: '2026-03-18',
      updated_at: '2026-03-25T17:30:00',
      tmr_days: 2,
   },
   {
      id: 45,
      os_number: 'OS-2026-0045',
      service_order_status: 'AGUARDANDO_PECAS',
      equipment_description: 'Bebedouro Garrafão',
      pcr_serial_number: 'BG-000091',
      requester_name: 'Dep. Comercial',
      technician_name: 'Lucas Teixeira',
      sla_due_date: '2026-03-20',
      sla_is_paused: true,
      is_sla_overdue: true,
      opened_at: '2026-03-15',
      updated_at: '2026-03-23T11:05:00',
      tmr_days: 10,
   },
   {
      id: 44,
      os_number: 'OS-2026-0044',
      service_order_status: 'AGUARDANDO_APROVACAO',
      equipment_description: 'Purificador Slim',
      pcr_serial_number: 'SL-000204',
      requester_name: 'Carlos Menezes',
      technician_name: 'Ana Rodrigues',
      sla_due_date: '2026-03-22',
      sla_is_paused: true,
      is_sla_overdue: true,
      opened_at: '2026-03-14',
      updated_at: '2026-03-22T08:50:00',
      tmr_days: 11,
   },
   {
      id: 43,
      os_number: 'OS-2026-0043',
      service_order_status: 'EM_TRIAGEM',
      equipment_description: 'Torneira Elétrica 3T',
      pcr_serial_number: null,
      requester_name: 'Roberto Alves',
      technician_name: null,
      sla_due_date: '2026-04-02',
      sla_is_paused: false,
      is_sla_overdue: false,
      opened_at: '2026-03-26',
      updated_at: '2026-03-26T08:01:00',
      tmr_days: 0,
   },
   {
      id: 42,
      os_number: 'OS-2026-0042',
      service_order_status: 'ENTREGUE',
      equipment_description: 'Purificador Soft Plus',
      pcr_serial_number: 'SP-000087',
      requester_name: 'Fernanda Souza',
      technician_name: 'Lucas Teixeira',
      sla_due_date: '2026-03-18',
      sla_is_paused: false,
      is_sla_overdue: false,
      opened_at: '2026-03-10',
      updated_at: '2026-03-24T16:20:00',
      tmr_days: 13,
   },
];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatDateToBrazilian(isoString) {
   if (!isoString) return '—';
   const dateOnly = isoString.substring(0, 10);
   const [year, month, day] = dateOnly.split('-');
   return `${day}/${month}/${year}`;
}

function formatDateTimeToBrazilian(isoString) {
   if (!isoString) return '—';
   const date = new Date(isoString);
   return date.toLocaleString('pt-BR', {
      day:    '2-digit',
      month:  '2-digit',
      year:   'numeric',
      hour:   '2-digit',
      minute: '2-digit',
   });
}

function getStatusLabel(statusKey) {
   return SERVICE_ORDER_STATUS_LABELS[statusKey] ?? statusKey;
}

function getStatusColors(statusKey) {
   return SERVICE_ORDER_STATUS_COLORS[statusKey] ?? SERVICE_ORDER_STATUS_COLORS.AGUARDANDO_RECEBIMENTO;
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────

export default function ServiceOrderList() {
   const navigate = useNavigate();

   const [serviceOrders, setServiceOrders] = useState(MOCK_SERVICE_ORDERS);
   // eslint-disable-next-line no-unused-vars
   const [isLoading, setIsLoading] = useState(false);
   const [currentPage, setCurrentPage] = useState(1);

   // Filtros
   const [filterOsNumber, setFilterOsNumber]         = useState('');
   const [filterStatus, setFilterStatus]             = useState('');
   const [filterTechnicianName, setFilterTechnicianName] = useState('');
   const [filterStartDate, setFilterStartDate]       = useState('');
   const [filterEndDate, setFilterEndDate]           = useState('');
   const [filterOnlyOverdue, setFilterOnlyOverdue]   = useState(false);

   const handleSearch = () => {
      // TODO: substituir pelo axios real quando backend estiver pronto
      // const params = { ... };
      // const response = await axios.get('/at/service-orders', { params });
      // setServiceOrders(response.data.serviceOrders);
      setCurrentPage(1);
      toast.info('Busca mock — integração com API pendente.');
   };

   const handleClearFilters = () => {
      setFilterOsNumber('');
      setFilterStatus('');
      setFilterTechnicianName('');
      setFilterStartDate('');
      setFilterEndDate('');
      setFilterOnlyOverdue(false);
      setCurrentPage(1);
      setServiceOrders(MOCK_SERVICE_ORDERS);
   };

   const handleRowClick = (serviceOrderId) => {
      navigate(`/assistencia-tecnica/ordens-de-servico/${serviceOrderId}`);
   };

   const handleNewServiceOrder = () => {
      navigate('/assistencia-tecnica/ordens-de-servico/nova');
   };

   // Filtragem client-side sobre os dados mock
   const filteredServiceOrders = useMemo(() => {
      return serviceOrders.filter((serviceOrder) => {
         const matchesOsNumber = filterOsNumber
            ? serviceOrder.os_number.toLowerCase().includes(filterOsNumber.toLowerCase())
            : true;

         const matchesStatus = filterStatus
            ? serviceOrder.service_order_status === filterStatus
            : true;

         const matchesTechnician = filterTechnicianName
            ? (serviceOrder.technician_name ?? '').toLowerCase().includes(filterTechnicianName.toLowerCase())
            : true;

         const matchesOverdue = filterOnlyOverdue
            ? serviceOrder.is_sla_overdue === true
            : true;

         return matchesOsNumber && matchesStatus && matchesTechnician && matchesOverdue;
      });
   }, [serviceOrders, filterOsNumber, filterStatus, filterTechnicianName, filterOnlyOverdue]);

   const totalPages = useMemo(
      () => Math.ceil(filteredServiceOrders.length / ITEMS_PER_PAGE),
      [filteredServiceOrders.length]
   );

   const currentPageServiceOrders = useMemo(() => {
      const indexOfLast  = currentPage * ITEMS_PER_PAGE;
      const indexOfFirst = indexOfLast - ITEMS_PER_PAGE;
      return filteredServiceOrders.slice(indexOfFirst, indexOfLast);
   }, [filteredServiceOrders, currentPage]);

   const handlePageChange = (pageNumber) => {
      if (!totalPages) return;
      const nextPage = Math.min(Math.max(1, pageNumber), totalPages);
      setCurrentPage(nextPage);
   };

   // Métricas rápidas calculadas sobre os dados mock
   const openTodayCount = useMemo(
      () => serviceOrders.filter((so) => so.opened_at === new Date().toISOString().substring(0, 10)).length,
      [serviceOrders]
   );

   const inProgressCount = useMemo(
      () => serviceOrders.filter((so) =>
         ['AGUARDANDO_RECEBIMENTO', 'EM_TRIAGEM', 'AGUARDANDO_PECAS', 'EM_REPARO', 'AGUARDANDO_APROVACAO', 'REPARO_CONCLUIDO'].includes(so.service_order_status)
      ).length,
      [serviceOrders]
   );

   const readyForDeliveryCount = useMemo(
      () => serviceOrders.filter((so) => so.service_order_status === 'PRONTO_PARA_ENTREGA').length,
      [serviceOrders]
   );

   const overdueCount = useMemo(
      () => serviceOrders.filter((so) => so.is_sla_overdue).length,
      [serviceOrders]
   );

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Ordens de Serviço</PageTitle>
               <PageSubtitle>
                  Assistência Técnica — Grupo MyShop Goiânia. Clique em uma OS para ver o
                  detalhamento completo.
               </PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               <button type="button" onClick={handleNewServiceOrder} disabled={isLoading}>
                  + Nova OS
               </button>
            </PageHeaderActions>
         </PageHeader>

         {/* ── Métricas rápidas ── */}
         <CardGrid $span={12} $mdSpan={6} $lgSpan={3} style={{ marginBottom: '16px' }}>
            <MiniCard>
               <MiniCardTitle>Abertas hoje</MiniCardTitle>
               <MiniCardValue>{openTodayCount}</MiniCardValue>
            </MiniCard>
            <MiniCard>
               <MiniCardTitle>Em andamento</MiniCardTitle>
               <MiniCardValue>{inProgressCount}</MiniCardValue>
            </MiniCard>
            <MiniCard>
               <MiniCardTitle>Prontas p/ entrega</MiniCardTitle>
               <MiniCardValue>{readyForDeliveryCount}</MiniCardValue>
            </MiniCard>
            <MiniCard>
               <MiniCardTitle>Em atraso</MiniCardTitle>
               <MiniCardValue style={{ color: overdueCount > 0 ? '#b71c1c' : 'inherit' }}>
                  {overdueCount}
               </MiniCardValue>
            </MiniCard>
         </CardGrid>

         {/* ── Filtros rápidos por status (tab bar) ── */}
         <FilterTabBar>
            {STATUS_FILTER_OPTIONS.map((statusOption) => (
               <FilterTab
                  key={statusOption.value}
                  $active={filterStatus === statusOption.value}
                  type="button"
                  onClick={() => {
                     setFilterStatus(statusOption.value);
                     setCurrentPage(1);
                  }}
               >
                  {statusOption.label}
               </FilterTab>
            ))}
         </FilterTabBar>

         {/* ── Filtros avançados ── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Filtros</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <FormGrid $cols={12}>
                  <Field className="col-3">
                     <label>Nº OS</label>
                     <input
                        type="text"
                        value={filterOsNumber}
                        onChange={(e) => setFilterOsNumber(e.target.value)}
                        placeholder="Ex.: OS-2026-0047"
                     />
                  </Field>

                  <Field className="col-3">
                     <label>Técnico responsável</label>
                     <input
                        type="text"
                        value={filterTechnicianName}
                        onChange={(e) => setFilterTechnicianName(e.target.value)}
                        placeholder="Nome do técnico..."
                     />
                  </Field>

                  <Field className="col-3">
                     <label>Data de abertura — início</label>
                     <input
                        type="date"
                        value={filterStartDate}
                        onChange={(e) => setFilterStartDate(e.target.value)}
                     />
                  </Field>

                  <Field className="col-3">
                     <label>Data de abertura — fim</label>
                     <input
                        type="date"
                        value={filterEndDate}
                        onChange={(e) => setFilterEndDate(e.target.value)}
                     />
                  </Field>

                  <Field className="col-3" style={{ justifyContent: 'flex-end' }}>
                     <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                           type="checkbox"
                           checked={filterOnlyOverdue}
                           onChange={(e) => setFilterOnlyOverdue(e.target.checked)}
                        />
                        Somente em atraso
                     </label>
                  </Field>

                  <Field className="col-9" $alignEnd>
                     <label>&nbsp;</label>
                     <ActionBar>
                        <ActionBarRight>
                           <button type="button" onClick={handleClearFilters} disabled={isLoading}>
                              Limpar
                           </button>
                           <button type="button" onClick={handleSearch} disabled={isLoading}>
                              Pesquisar
                           </button>
                        </ActionBarRight>
                     </ActionBar>
                  </Field>
               </FormGrid>
            </SectionBody>
         </Section>

         {/* ── Tabela de resultados ── */}
         <Section>
            <SectionHeader>
               <SectionHeaderRow>
                  <SectionTitle>Resultados</SectionTitle>
                  <SectionMeta
                     items={[
                        { key: 'count', label: 'Total:', value: filteredServiceOrders.length },
                        { key: 'page',  label: 'Página', value: `${currentPage}/${totalPages || 1}` },
                     ]}
                  />
               </SectionHeaderRow>
            </SectionHeader>

            <SectionBody>
               {currentPageServiceOrders.length ? (
                  <>
                     <TableWrapper $minWidth="900px">
                        <Table>
                           <thead>
                              <tr>
                                 <th>Nº OS</th>
                                 <th>Equipamento</th>
                                 <th>Série / Tipo</th>
                                 <th>Solicitante</th>
                                 <th>Status</th>
                                 <th>Técnico</th>
                                 <th className="center">Abertura</th>
                                 <th className="center">Últ. atualização</th>
                                 <th className="center">SLA / TMR</th>
                              </tr>
                           </thead>
                           <tbody>
                              {currentPageServiceOrders.map((serviceOrder) => (
                                 <tr
                                    key={String(serviceOrder.id)}
                                    onClick={() => handleRowClick(serviceOrder.id)}
                                    title="Clique para abrir a OS"
                                 >
                                    <td style={{ fontWeight: '700' }}>
                                       {serviceOrder.os_number}
                                    </td>
                                    <td>{serviceOrder.equipment_description}</td>
                                    <td>
                                       {serviceOrder.pcr_serial_number ?? (
                                          <span style={{ color: '#888', fontStyle: 'italic' }}>
                                             S/ série
                                          </span>
                                       )}
                                    </td>
                                    <td>{serviceOrder.requester_name}</td>
                                    <td>
                                       <ServiceOrderStatusPill
                                          $statusColor={getStatusColors(serviceOrder.service_order_status)}
                                       >
                                          {getStatusLabel(serviceOrder.service_order_status)}
                                       </ServiceOrderStatusPill>
                                    </td>
                                    <td>
                                       {serviceOrder.technician_name ?? (
                                          <span style={{ color: '#aaa' }}>—</span>
                                       )}
                                    </td>
                                    <td className="center">
                                       {formatDateToBrazilian(serviceOrder.opened_at)}
                                    </td>
                                    <td className="center">
                                       {formatDateTimeToBrazilian(serviceOrder.updated_at)}
                                    </td>
                                    <td className="center">
                                       {serviceOrder.is_sla_overdue ? (
                                          <SlaAlertBadge>
                                             ⚠ Atrasada
                                          </SlaAlertBadge>
                                       ) : (
                                          <span style={{ color: '#888' }}>
                                             {serviceOrder.tmr_days}d
                                          </span>
                                       )}
                                    </td>
                                 </tr>
                              ))}
                           </tbody>
                        </Table>
                     </TableWrapper>

                     <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        disabled={isLoading}
                     />
                  </>
               ) : (
                  <EmptyState
                     title="Nenhuma OS encontrada"
                     description="Ajuste os filtros ou abra uma nova ordem de serviço."
                  />
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
